// libraries
import { useCallback, useEffect, useRef, useState } from 'react';
import {
	Button,
	Form,
	FormInstance,
	Input,
	Modal,
	ModalFuncProps,
	notification,
	Select,
	Switch,
	Table,
	Tabs,
	Tooltip,
} from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import styled from 'styled-components';

// resources
import * as MunicipalityService from '../../../services/bb-api/municipality';
import { txt } from '../../../locales/i18n';

export type CreateVolunteerModalProps = Omit<ModalFuncProps, 'onOk'> & {
	createVolunteer: (values: any) => Promise<void>;
	linkVolunteer: (userId: number, juniorAccess: boolean) => Promise<void>;
	getUsersCallback: (email: string) => Promise<Record<string, any>[]>;
	showJuniorSwitch?: boolean;
};

export function CreateVolunteerModal(props: CreateVolunteerModalProps) {
	const [loading, setLoading] = useState<boolean>(false);
	const [municipalities, setMunicipalities] = useState<{ label: string; value: number }[]>([]);
	const [emailSearch, setEmailSearch] = useState<string>('');
	const [users, setUsers] = useState<Record<string, any>[]>([]);
	const [selectedUser, setSelectedUser] = useState<React.Key | null>(null);
	const [linkJuniorAccess, setLinkJuniorAccess] = useState<boolean>(false);

	const formRef = useRef<FormInstance>(null);

	const getMunicipalities = useCallback(async () => {
		try {
			const municipalities: Record<string, any>[] = await MunicipalityService.query();
			const municipalitiesOptions: any[] = municipalities.map(({ id, name }: Record<string, any>) => {
				return { label: name, value: id };
			});

			setMunicipalities(municipalitiesOptions);
		} catch (error) {
			const message: string = (error as Error).name;
			const description: string = (error as Error).message;
			notification.error({ message, description });
		}
	}, []);

	useEffect(() => {
		getMunicipalities();
	}, [getMunicipalities]);

	async function getUsers() {
		try {
			const users: Record<string, any>[] = await props.getUsersCallback(emailSearch);
			setUsers(users);
		} catch (error) {
			const message: string = (error as Error).name;
			const description: string = (error as Error).message;
			notification.error({ message, description });
		}
	}

	async function handleFinish(values: any): Promise<void> {
		const form: FormInstance | null = formRef.current;
		if (form === null) return;

		setLoading(true);
		form.setFields([{ name: 'email', errors: [] }]);

		try {
			await props.createVolunteer?.(values);
			notification.success({ message: txt('notification.create successful') });
			handleCloseModal();
		} catch (error) {
			const errorMessage: string = (error as Error).message;

			if (errorMessage === 'EMAIL_ALREADY_TAKEN') {
				notification.error({ message: txt('notification.email occupied') });
				form.setFields([{ name: 'email', errors: [txt('notification.email occupied')] }]);
				setLoading(false);
				return;
			}

			notification.error({ message: txt('notification.create failed') });
		}

		setLoading(false);
	}

	const rowSelection = {
		getCheckboxProps: (record: Record<string, any>) => ({ disabled: record.volunteer !== null }),
		onChange: ([selectedRowKey]: React.Key[]) => {
			setSelectedUser(selectedRowKey);
		},
	};

	async function linkVolunteer(): Promise<void> {
		if (selectedUser === null) {
			notification.warning({ message: txt('red-cross.no user selected') });
			return;
		}

		setLoading(true);
		await props.linkVolunteer?.(Number(selectedUser), linkJuniorAccess);
		setLoading(false);
		handleCloseModal();
	}

	function handleCloseModal(): void {
		setEmailSearch('');
		setSelectedUser(null);
		setLinkJuniorAccess(false);
		props.onCancel?.();
	}

	return (
		<Modal
			visible={props.visible}
			destroyOnClose
			maskClosable
			onCancel={handleCloseModal}
			closable={false}
			footer={null}
		>
			<Tabs defaultActiveKey="" centered>
				<Tabs.TabPane tab={txt('red-cross.add new user')} key="">
					<Form
						ref={formRef}
						onFinish={handleFinish}
						initialValues={{ municipality_id: 1, juniorAccess: false }}
					>
						<Tooltip title={txt('global.username')}>
							<Form.Item name="username">
								<Input required placeholder={txt('global.username')} autoFocus />
							</Form.Item>
						</Tooltip>

						<Tooltip title={txt('global.municipality')}>
							<Form.Item name="municipality_id">
								<Select
									placeholder={txt('global.municipality')}
									optionFilterProp="label"
									dropdownMatchSelectWidth={false}
									options={municipalities}
									showSearch
								/>
							</Form.Item>
						</Tooltip>

						<Tooltip title={txt('global.birth year')}>
							<Form.Item name="birth_year">
								<Input
									required
									placeholder={txt('global.birth year')}
									type="number"
									min={1920}
									max={new Date().getFullYear() - 15}
								/>
							</Form.Item>
						</Tooltip>

						<Tooltip title="Email">
							<Form.Item hasFeedback name="email">
								<Input required placeholder="Email" />
							</Form.Item>
						</Tooltip>

						<Form.Item>
							<div style={{ display: 'flex', gap: '1ch', justifyContent: 'end' }}>
								{props.showJuniorSwitch && (
									<Form.Item
										name="juniorAccess"
										style={{ marginRight: 'auto' }}
										valuePropName="checked"
									>
										<StyledSwitch checkedChildren="Junior" unCheckedChildren="Junior" />
									</Form.Item>
								)}

								<Button onClick={handleCloseModal}>{txt('global.cancel')}</Button>

								<Button type="primary" htmlType="submit">
									{txt('global.create')}
								</Button>
							</div>
						</Form.Item>
					</Form>
				</Tabs.TabPane>

				<Tabs.TabPane tab={txt('red-cross.search existing users')} key="1">
					<StyledUserSearch>
						<Form>
							<div className="user-search-container">
								<Tooltip title="Email">
									<Form.Item name="email" noStyle>
										<Input
											placeholder="Email"
											autoFocus
											value={emailSearch}
											onChange={({ target }) => setEmailSearch(target.value)}
											allowClear
										/>
									</Form.Item>
								</Tooltip>

								<Button type="primary" htmlType="submit" loading={loading} onClick={getUsers}>
									{txt('global.search')}
								</Button>
							</div>
						</Form>

						<Table
							rowKey="id"
							loading={loading}
							dataSource={users}
							rowSelection={{ type: 'radio', ...rowSelection }}
							pagination={{ pageSize: 100, position: [] }}
						>
							<Table.Column title="Email" dataIndex="email" ellipsis />
							<Table.Column title={txt('global.username')} dataIndex="username" ellipsis />
							<Table.Column
								dataIndex="volunteer"
								width="4ch"
								render={(volunteer) => (volunteer ? <CheckOutlined /> : null)}
							/>
						</Table>

						<div style={{ display: 'flex', gap: '1ch', justifyContent: 'end' }}>
							{props.showJuniorSwitch && (
								<StyledSwitch
									checked={linkJuniorAccess}
									onChange={setLinkJuniorAccess}
									checkedChildren="Junior"
									unCheckedChildren="Junior"
									style={{ marginRight: 'auto' }}
								/>
							)}

							<Button type="ghost" onClick={handleCloseModal}>
								{txt('global.cancel')}
							</Button>

							<Button type="primary" loading={loading} onClick={linkVolunteer}>
								{txt('global.create')}
							</Button>
						</div>
					</StyledUserSearch>
				</Tabs.TabPane>
			</Tabs>
		</Modal>
	);
}

const StyledUserSearch = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1ch;

	.user-search-container {
		display: flex;
		gap: 1ch;
	}
`;

const StyledSwitch = styled(Switch)`
	zoom: 1.4;

	span {
		font-size: 0.65em;
	}
`;
